import {
  ApiEnvelope,
  Case,
  ClinicalData,
  ClinicalDataRecord,
  DraftCase,
  TxCenterORData,
  TxCenterORInitialData,
} from '@/api/apis/case';
import { queryClient } from '@/queries';
import { caseQueries } from '@/queries/case/case.query';
import { reconcileCaseAcrossListCaches } from './case-list-reconciliation.utils';

const isApiEnvelope = <T>(v: unknown): v is ApiEnvelope<T> => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Partial<ApiEnvelope<unknown>> & Record<string, unknown>;
  return 'data' in o && ('hasData' in o || 'hasError' in o || 'hasNotError' in o);
};

export const toClinicalRecord = (resp: unknown): ClinicalDataRecord => {
  if (isApiEnvelope<ClinicalDataRecord>(resp)) return resp.data;
  if (typeof resp === 'object' && resp !== null && 'clinicalItemType' in resp) {
    return resp as ClinicalDataRecord;
  }
  throw new Error('Unexpected clinical mutation response shape');
};

export const mergeClinicalRecords = (
  record: ClinicalDataRecord,
  existingClinicalData: ClinicalData = [],
): ClinicalData => {
  const filtered = existingClinicalData.filter((cd) => cd.clinicalItemType !== record.clinicalItemType);
  return [...filtered, record];
};

export const updateClinicalInCaseCache = (caseId: string, updatedRecord: ClinicalDataRecord) => {
  queryClient.setQueryData(caseQueries.item(caseId).queryKey, (prev: Case | DraftCase | undefined) => {
    if (!prev) return prev;
    const nextClinical = mergeClinicalRecords(updatedRecord, prev.clinicalData);
    return { ...prev, clinicalData: nextClinical };
  });

  const updated = queryClient.getQueryData<Case | DraftCase>(caseQueries.item(caseId).queryKey);
  if (updated) {
    reconcileCaseAcrossListCaches(updated as Case);
  }
};

export const updateTxCenterOR = async ({
  updateMutation,
  caseId,
  data,
}: {
  updateMutation: ({ caseId, ...restVars }: Pick<Case, 'caseId'> & Partial<TxCenterORData>) => Promise<Case>;
  caseId: string;
  data: TxCenterORInitialData;
}): Promise<Case | DraftCase> => {
  return updateMutation({ caseId, ...data });
};
